import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  getClassicModels,
  getGenerationCreditCost,
  INTEGRATED_MODEL,
  INTEGRATED_MODEL_ID,
  AFFILIATE_DEFAULT_MODEL_ID,
  INTEGRATED_DURATION_MIN,
  INTEGRATED_DURATION_MAX,
  POWER_TIER_LABEL,
  CLASSIC_TIER_LABEL,
  type ModelId,
} from "@/config/models";
import { useUserStore } from "@/stores/user-store";
import { cn } from "@/lib/utils";
import { Zap, Gem, Crown, Sparkles, Lock, AudioLines } from "lucide-react";

interface ModelSelectorProps {
  value: ModelId;
  onChange: (id: ModelId) => void;
  integratedSeconds: number;
  onIntegratedSecondsChange: (seconds: number) => void;
  /** Affiliate free-credit path — only Classic Standard 5s is selectable. */
  lockToAffiliateDefault?: boolean;
  /**
   * When set (signed-out traffic), locked-model taps open Sign Up instead of
   * the purchase upsell — anonymous users can't buy a pack yet.
   */
  onRequireAuth?: () => void;
}

export function ModelSelector({
  value,
  onChange,
  integratedSeconds,
  onIntegratedSecondsChange,
  lockToAffiliateDefault = false,
  onRequireAuth,
}: ModelSelectorProps) {
  const { hasPurchased } = useUserStore();
  const powerLocked = !hasPurchased;
  const isIntegrated = value === INTEGRATED_MODEL_ID;
  const [showUpgradeModal, setShowUpgradeModal] = useState<"power" | "affiliate" | "premium" | null>(null);

  const classicModels = getClassicModels();
  const integratedCost = getGenerationCreditCost(INTEGRATED_MODEL, integratedSeconds);

  const selectModel = (id: ModelId) => {
    const gateLocked =
      (id === INTEGRATED_MODEL_ID && powerLocked) ||
      (lockToAffiliateDefault && id !== AFFILIATE_DEFAULT_MODEL_ID) ||
      (!!classicModels.find((m) => m.id === id)?.requiresPurchase && !hasPurchased);

    if (gateLocked) {
      if (onRequireAuth) {
        onRequireAuth();
        return;
      }
      if (id === INTEGRATED_MODEL_ID && powerLocked) {
        setShowUpgradeModal("power");
        return;
      }
      if (lockToAffiliateDefault && id !== AFFILIATE_DEFAULT_MODEL_ID) {
        setShowUpgradeModal("affiliate");
        return;
      }
      setShowUpgradeModal("premium");
      return;
    }
    onChange(id);
  };

  const upgradeCopy =
    showUpgradeModal === "power"
      ? {
          title: `Unlock ${POWER_TIER_LABEL}`,
          body: (
            <>Purchase any star pack to unlock <strong className="text-[#2d3127]">{POWER_TIER_LABEL}</strong> — 720p video with native audio, 5–15s.</>
          ),
        }
      : showUpgradeModal === "affiliate"
        ? {
            title: "Unlock More Models",
            body: (
              <>Your free stars cover <strong className="text-[#2d3127]">Classic Standard 5s</strong>. Purchase any pack to unlock {POWER_TIER_LABEL}, Premium, and 8s.</>
            ),
          }
        : {
            title: "Unlock 8-Second Videos",
            body: (
              <>Purchase any star pack to unlock <strong className="text-[#2d3127]">8-second</strong> classic generations.</>
            ),
          };

  return (
    <div className="space-y-4">
      {lockToAffiliateDefault && (
        <p className="text-[11px] text-[#8a8570] leading-relaxed">
          {onRequireAuth ? (
            <>
              Free stars cover{" "}
              <span className="text-[#2d3127] font-semibold">Classic Standard 5s</span>
              . Sign up free to generate — unlock {POWER_TIER_LABEL} after any pack.
            </>
          ) : (
            <>
              Free stars cover{" "}
              <span className="text-[#2d3127] font-semibold">Classic Standard 5s</span>
              . Buy a pack to unlock other models.
            </>
          )}
        </p>
      )}

      <div>
        <p className="text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">
          {POWER_TIER_LABEL}
        </p>
        <button
          type="button"
          onClick={() => selectModel(INTEGRATED_MODEL_ID)}
          className={cn(
            "group relative w-full overflow-hidden rounded-xl border-2 text-left transition-all",
            isIntegrated
              ? "border-orange-400/70 shadow-[0_0_24px_rgba(232,130,46,0.18)]"
              : "border-orange-200/80 hover:border-orange-300",
          )}
          style={{
            background: isIntegrated
              ? "linear-gradient(135deg, #fff4e8 0%, #ffe8d4 40%, #ffd9b8 100%)"
              : "linear-gradient(135deg, rgba(232,130,46,0.1) 0%, rgba(255,255,255,0.95) 55%, #fff 100%)",
          }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 10% 0%, rgba(255,200,120,0.45), transparent 42%), radial-gradient(ellipse at 100% 100%, rgba(232,130,46,0.12), transparent 45%)",
            }}
          />
          <div className="relative flex items-center gap-3 p-3.5">
            <div
              className={cn(
                "size-10 rounded-xl flex items-center justify-center shrink-0 border",
                isIntegrated
                  ? "bg-[#e8822e]/15 border-[#e8822e]/30 text-[#e8822e] shadow-[0_0_10px_rgba(232,130,46,0.2)]"
                  : "bg-[#e8822e]/8 border-[#e8822e]/15 text-[#e8822e]/80",
              )}
            >
              {powerLocked ? <Lock className="size-4" strokeWidth={1.8} /> : <AudioLines className="size-4" strokeWidth={1.8} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={cn("text-[13px] font-bold", isIntegrated ? "text-[#2d3127]" : "text-[#5a5545]")}>
                  {INTEGRATED_MODEL.label}
                </p>
                <span className="text-[9px] font-bold uppercase tracking-wider text-white bg-[#e8822e] px-1.5 py-0.5 rounded">
                  PRO
                </span>
              </div>
              <p className="text-[11px] text-[#8a8570] mt-0.5 flex items-center gap-1.5">
                <Sparkles className="size-3 shrink-0 text-[#e8822e]" strokeWidth={2} />
                native audio · {INTEGRATED_DURATION_MIN}–{INTEGRATED_DURATION_MAX}s
              </p>
            </div>
            {isIntegrated && !powerLocked ? (
              <span className="text-[12px] font-semibold text-[#e8822e] tabular-nums shrink-0">
                {integratedCost} ★
              </span>
            ) : powerLocked ? (
              <span className="text-[10px] font-bold text-[#e8822e] shrink-0">Unlock</span>
            ) : null}
          </div>
        </button>

        {isIntegrated && !powerLocked && (
          <div className="mt-2 rounded-xl border border-[#e8822e]/30 bg-[#e8822e]/[0.05] p-3.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8a8570]">Clip length</p>
              <p className="text-[13px] font-bold text-[#2d3127] tabular-nums">
                {integratedSeconds}s
                <span className="text-[#8a8570] font-semibold ml-2">· {integratedCost} ★</span>
              </p>
            </div>
            <input
              type="range"
              min={INTEGRATED_DURATION_MIN}
              max={INTEGRATED_DURATION_MAX}
              step={1}
              value={integratedSeconds}
              onChange={(e) => onIntegratedSecondsChange(Number(e.target.value))}
              className="w-full accent-[#e8822e] h-1.5 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-[#b5b0a0] tabular-nums">
              <span>{INTEGRATED_DURATION_MIN}s</span>
              <span>{INTEGRATED_DURATION_MAX}s</span>
            </div>
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">
          {CLASSIC_TIER_LABEL}
        </p>
        {hasPurchased && !isIntegrated && (
          <button
            type="button"
            onClick={() => onChange(INTEGRATED_MODEL_ID)}
            className="mb-2 flex w-full items-start gap-2.5 rounded-xl border border-[#e8822e]/25 bg-[#e8822e]/[0.06] p-2.5 text-left transition-colors hover:bg-[#e8822e]/[0.1]"
          >
            <Sparkles className="size-3.5 shrink-0 text-[#e8822e] mt-0.5" strokeWidth={2} />
            <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-[#5a5545]">
              Try <span className="font-bold text-[#2d3127]">{POWER_TIER_LABEL}</span> for native audio · 5–15s
            </span>
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-[#e8822e]">
              Switch
            </span>
          </button>
        )}
        <div className="grid grid-cols-2 gap-2">
          {classicModels.map((model) => {
            const Icon = model.tier === "Premium" ? Gem : Zap;
            const isSelected = value === model.id;
            const affiliateLocked = lockToAffiliateDefault && model.id !== AFFILIATE_DEFAULT_MODEL_ID;
            const locked = affiliateLocked || (!!model.requiresPurchase && !hasPurchased);
            return (
              <button
                key={model.id}
                type="button"
                onClick={() => selectModel(model.id)}
                className={cn(
                  "relative flex flex-col items-center p-3 rounded-xl border-2 transition-all text-center",
                  isSelected
                    ? "bg-orange-50 border-orange-300/60"
                    : "bg-white border-[rgba(45,49,39,0.06)] hover:border-[rgba(45,49,39,0.12)] hover:bg-[rgba(45,49,39,0.02)]",
                  affiliateLocked && "opacity-60",
                )}
              >
                {affiliateLocked ? (
                  <Lock className="absolute top-2 right-2 size-3 text-[#b5b0a0]" strokeWidth={2} />
                ) : locked ? (
                  <span className="absolute -top-2 right-2 rounded-full bg-[#e8822e] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                    PRO
                  </span>
                ) : null}
                <Icon className={cn("size-3.5 mb-1", isSelected ? "text-[#e8822e]" : "text-[#b5b0a0]")} strokeWidth={1.8} />
                <span className={cn("text-[12px] font-bold", isSelected ? "text-[#2d3127]" : "text-[#5a5545]")}>
                  {model.label}
                </span>
                <span className="text-[10px] text-[#8a8570] tabular-nums">{model.creditCost} ★</span>
              </button>
            );
          })}
        </div>
      </div>

      {showUpgradeModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowUpgradeModal(null)} />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white border border-[rgba(45,49,39,0.08)] shadow-xl p-6 text-center animate-modal-in">
            <div className="size-12 rounded-xl bg-orange-50 border border-orange-200/60 flex items-center justify-center mx-auto mb-4">
              <Crown className="size-6 text-[#e8822e]" />
            </div>
            <h3 className="text-lg font-bold text-[#2d3127] mb-1.5">{upgradeCopy.title}</h3>
            <p className="text-[13px] text-[#8a8570] leading-relaxed mb-5">{upgradeCopy.body}</p>
            <Link
              to="/billing"
              className="btn-primary w-full py-3 rounded-xl text-[14px] flex items-center justify-center gap-2"
            >
              <Sparkles className="size-4" />
              Get Stars
            </Link>
            <button
              onClick={() => setShowUpgradeModal(null)}
              className="w-full py-2.5 mt-1 text-[13px] font-semibold text-[#8a8570] hover:text-[#2d3127] transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
